import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Loader2, Plus } from "lucide-react";
import QuantitySelector from "@/components/billing/QuantitySelector";
import RefundNotice from "@/components/billing/RefundNotice";
import { usd } from "@/lib/billingCheckout";

/**
 * An extra quote, as its own card beside the plans (PLANES M4b).
 *
 * Every number on it comes from `/billing/config`: the price of one extra
 * (platform_config `extra_credit_price_usd`), the most one purchase may buy
 * (`extra_credit_max_per_purchase`, which the server clamps to as well), and
 * the per-quote cost of Pro and Master — each plan's price over its quotes a
 * month, so the comparison moves by itself when either one is repriced.
 *
 * `mode="buy"` is the billing page: the shared quantity selector (presets and
 * a free amount up to the cap), the live total, the purchase. `mode="info"` is the public pricing page,
 * which cannot sell to a visitor: the same facts and a way in (`action`).
 */
export default function ExtraQuoteCard({
  price, max = 1, plans = [], mode = "buy", onBuy, pending = false, action = null,
}) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const cap = Math.max(1, Number(max) || 1);

  const perQuote = (planId) => {
    const plan = plans.find((p) => p.plan_id === planId);
    const quotes = Number(plan?.included_quotes_per_month);
    const monthly = Number(plan?.price_monthly_usd);
    return plan && quotes > 0 ? usd(monthly / quotes) : null;
  };
  const pro = perQuote("plan_pro");
  const master = perQuote("plan_master");

  return (
    <div className="card-base p-6" data-testid="extra-quote-card">
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <Plus size={22} className="text-hive-ink" />
            <h3 className="font-display text-2xl font-bold text-worker">{t("extraQuote.title")}</h3>
          </div>
          <p className="mt-2 text-smoke">{t("extraQuote.body")}</p>
          <p className="mt-3 font-mono text-3xl font-bold text-worker" data-testid="extra-quote-price">
            {t("extraQuote.price", { price: usd(price) })}
          </p>
          {pro && master && (
            <p className="mt-2 text-sm text-smoke" data-testid="extra-quote-compare">
              {t("extraQuote.compare", { pro, master })}
            </p>
          )}
          <p className="mt-2 text-xs font-display font-semibold text-worker" data-testid="extra-quote-nonrefundable">
            {t("extraQuote.nonRefundable")}
          </p>
        </div>

        {mode === "buy" ? (
          <div className="flex flex-col items-stretch gap-3 w-full sm:w-auto sm:min-w-[260px]">
            <QuantitySelector value={qty} onChange={setQty} max={cap} testId="extra-quote-qty" />
            <p className="text-sm text-smoke font-mono" data-testid="extra-quote-total">
              {t("billingX.totalLabel")}{" "}
              <span className="font-bold text-worker">{qty ? usd(Number(price) * qty) : "—"}</span>
            </p>
            <button
              type="button"
              onClick={() => qty && onBuy?.(qty)}
              disabled={pending || !qty}
              className="fx-btn fx-btn--primary"
              data-testid="extra-quote-buy"
            >
              {pending
                ? <><Loader2 className="animate-spin" size={16} /> {t("billingX.redirecting")}</>
                : <><CreditCard size={16} /> {t("extraQuote.buy", { count: qty || 1 })}</>}
            </button>
            <RefundNotice testId="extra-quote-refund-notice" />
          </div>
        ) : (
          <div className="flex items-center">{action}</div>
        )}
      </div>
    </div>
  );
}
